"use client";

import React, { useState, useMemo } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type PosterGroup = "Remodelling" | "Flooring" | "Painting" | "Plumbing" | "Exterior";
interface PosterItem {
  id: string;
  title: string;
  category: string;
  group: PosterGroup;
  image: string;
}
const posters: PosterItem[] = [{
  id: "p1",
  title: "Modern kitchen overhaul",
  category: "Remodelling",
  group: "Remodelling",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685663/img/kitchen_remodel_ablaze.png"
}, {
  id: "p2",
  title: "Custom backsplash install",
  category: "Tiling",
  group: "Flooring",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685663/img/tiling_backsplash_ablaze.png"
}, {
  id: "p3",
  title: "Interior color refresh",
  category: "Painting",
  group: "Painting",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685663/img/interior_painting_ablaze.png"
}, {
  id: "p4",
  title: "Driveway restoration",
  category: "Power washing",
  group: "Exterior",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685663/img/power_washing_driveway_ablaze.png"
}, {
  id: "p5",
  title: "Luxury vinyl plank install",
  category: "Vinyl flooring",
  group: "Flooring",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685663/img/vinyl_flooring_ablaze.png"
}, {
  id: "p6",
  title: "Full bathroom repiping",
  category: "Plumbing",
  group: "Plumbing",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685663/img/plumbing_bathroom_ablaze.png"
}, {
  id: "p7",
  title: "Premium laminate layout",
  category: "Laminate flooring",
  group: "Flooring",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685663/img/laminate_flooring_ablaze.png"
}, {
  id: "p8",
  title: "Classic oak refinishing",
  category: "Hardwood flooring",
  group: "Flooring",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685663/img/hardwood_flooring_ablaze.png"
}, {
  id: "p9",
  title: "Carpentry framing and trim",
  category: "Carpentry",
  group: "Remodelling",
  image: "https://ablazecontractingservicellc.com/wp-content/uploads/2026/06/IMG-20260625-WA0003-1.jpg?v=dj2tk70q24"
}, {
  id: "p10",
  title: "Drywall repairs and installation",
  category: "Drywall",
  group: "Remodelling",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685663/img/drywall_repair_ablaze.png"
}, {
  id: "p11",
  title: "Snow removal",
  category: "Snow removal",
  group: "Exterior",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685663/img/snow_removal_ablaze.png"
}];
const FILTER_GROUPS: {
  id: string;
  label: string;
  group?: PosterGroup;
}[] = [{
  id: "all",
  label: "All"
}, {
  id: "Remodelling",
  label: "Remodelling",
  group: "Remodelling"
}, {
  id: "Flooring",
  label: "Flooring",
  group: "Flooring"
}, {
  id: "Painting",
  label: "Painting",
  group: "Painting"
}, {
  id: "Plumbing",
  label: "Plumbing",
  group: "Plumbing"
}, {
  id: "Exterior",
  label: "Exterior",
  group: "Exterior"
}];
export default function MasterpiecesInProgress() {
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const visibleChips = useMemo(() => {
    return FILTER_GROUPS.filter(chip => {
      if (chip.id === "all") return true;
      return posters.some(p => p.group === chip.group);
    });
  }, []);
  const handleFilterChange = (filterId: string) => {
    setActiveFilter(filterId);
    setIsExpanded(false);
  };
  const filteredPosters = useMemo(() => {
    if (activeFilter === "all") return posters;
    return posters.filter(p => p.group === activeFilter);
  }, [activeFilter]);
  const displayedPosters = useMemo(() => {
    if (isExpanded) return filteredPosters;
    return filteredPosters.slice(0, 6);
  }, [filteredPosters, isExpanded]);
  const hasMore = filteredPosters.length > 6;
  return <section data-section-id="238960" id="recent-work" className="bg-background text-foreground py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 md:mb-12">
          <div className="max-w-2xl text-left">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-normal leading-[1.15] tracking-tight mb-3 text-foreground">
              Recent work
            </h2>
            <p className="text-base md:text-lg text-muted-foreground font-sans leading-relaxed">
              Recent project photos from jobs completed in Dumfries and nearby communities.
            </p>
          </div>

          <Link to="/ProjectGalleryPage" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-primary hover:text-primary/80 transition-colors py-2">
            <span>View full gallery</span>
            <ArrowRight className="size-3.5" />
          </Link>
        </div>

        {/* Filter Chips */}
        <div role="tablist" aria-label="Filter projects by category" className="flex items-center gap-2 overflow-x-auto pb-4 mb-8 no-scrollbar">
          {visibleChips.map(chip => {
          const isActive = activeFilter === chip.id;
          return <button key={chip.id} type="button" role="tab" aria-selected={isActive} onClick={() => handleFilterChange(chip.id)} className={`min-h-[44px] px-4 py-2 rounded-xs font-mono uppercase text-xs tracking-wider transition-colors shrink-0 flex items-center justify-center font-medium ${isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground border border-border hover:border-primary/50 hover:text-foreground"}`}>
                {chip.label}
              </button>;
        })}
        </div>

        {/* Project grid */}
        <div ref={gridRef} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-opacity duration-300 ${isGridVisible ? "opacity-100" : "opacity-0 motion-reduce:opacity-100"}`}>
          {displayedPosters.map((poster, index) => <div key={poster.id} data-index={index} className="border border-border bg-card rounded-xs overflow-hidden flex flex-col">
              <div className="relative aspect-[4/3] bg-muted overflow-hidden">
                <img src={poster.image} data-wvc-srcset={`${poster.image}-wvc-srcset`} data-wvc-sizes={`${poster.image}-wvc-sizes`} alt={poster.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
              </div>
              <div className="p-4 flex flex-col flex-1 justify-between gap-2 border-t border-border">
                <div>
                  <span className="text-primary font-mono text-xs tracking-widest uppercase block mb-1">
                    {poster.category}
                  </span>
                  <h3 className="text-foreground font-serif text-base font-normal leading-snug">
                    {poster.title}
                  </h3>
                </div>
                <div className="pt-2">
                  <Link to="/ProjectGalleryPage" className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1">
                    <span>View project</span>
                    <ArrowRight className="size-3" />
                  </Link>
                </div>
              </div>
            </div>)}
        </div>

        {/* Show More / Show Fewer Button */}
        {hasMore && <div className="flex justify-center mt-8">
            <Button type="button" variant="outline" size="lg" onClick={() => setIsExpanded(prev => !prev)} className="min-h-[44px] border-border bg-secondary text-secondary-foreground hover:bg-accent font-mono text-xs uppercase tracking-wider rounded-xs">
              {isExpanded ? <>
                  <ChevronUp className="size-4 mr-2" />
                  Show fewer
                </> : <>
                  <ChevronDown className="size-4 mr-2" />
                  Show more ({filteredPosters.length - 6} more)
                </>}
            </Button>
          </div>}
      </div>
    </section>;
}